import { NotFoundError } from "../../types";
import { PermissionError, ValidationError } from "../errors";
import { isEmail, normaliseEmail } from "../logic/codes";
import { requestStatus } from "../logic/invitations";
import { IDENTITY_POLICY } from "../policy";
import { TEAM_SIZES, type IdentityApi } from "../types";
import { requireOrg, requirePermission, requireSession, roleIn, type Ctx } from "./context";
import { orgRequestView, projectRequestView } from "./views";

/** Access requests: firms asking to join Orthos (FR-ADMIN-052) and people asking for a project (FR-ADMIN-028). */

type RequestApi = Pick<
  IdentityApi,
  | "requestOrganisationAccess"
  | "requestProjectAccess"
  | "myProjectAccessRequest"
  | "listProjectAccessRequests"
  | "decideProjectAccessRequest"
  | "selfAddToProject"
>;

const FIELD_MAX = 120;
const MESSAGE_MAX = 1_000;

function required(value: string, field: string, label: string): string {
  const clean = value.trim();
  if (clean === "") throw new ValidationError(field, `Enter ${label}.`);
  if (clean.length > FIELD_MAX)
    throw new ValidationError(field, `Use ${String(FIELD_MAX)} characters or fewer.`);
  return clean;
}

export function createRequestApi(ctx: Ctx): RequestApi {
  return {
    async selfAddToProject(projectId, reason) {
      await ctx.wait();
      const { org, user, role } = requireOrg(ctx);
      if (role !== "owner" && role !== "admin")
        throw new PermissionError(
          "Owner or Admin role needed to add yourself to a confidential project.",
        );
      const clean = reason.trim();
      if (clean === "") throw new ValidationError("reason", "Say why you need access.");
      if (clean.length > MESSAGE_MAX)
        throw new ValidationError("reason", `Use ${String(MESSAGE_MAX)} characters or fewer.`);
      ctx.state.projectGrants = ctx.state.projectGrants.filter(
        (g) => !(g.projectId === projectId && g.userId === user.id),
      );
      ctx.state.projectGrants.push({
        organisationId: org.id,
        projectId,
        userId: user.id,
        role: "project_admin",
      });
      ctx.commit();
    },
    async requestOrganisationAccess(input) {
      await ctx.wait();
      const email = normaliseEmail(input.email);
      if (!isEmail(email))
        throw new ValidationError("email", "Enter a work email like name@firm.in.");
      if (!TEAM_SIZES.includes(input.teamSize))
        throw new ValidationError("teamSize", "Choose your team size.");
      if (input.disciplines.length === 0)
        throw new ValidationError("disciplines", "Choose at least one kind of work.");
      if (input.message.length > MESSAGE_MAX)
        throw new ValidationError("message", `Use ${String(MESSAGE_MAX)} characters or fewer.`);
      // No account and no WorkOS user are created: the request only queues for staff (FR-ADMIN-052).
      const request = {
        id: `req-${ctx.token().slice(0, 12)}`,
        name: required(input.name, "name", "your name"),
        email,
        firm: required(input.firm, "firm", "your firm's name"),
        city: required(input.city, "city", "your city"),
        teamSize: input.teamSize,
        disciplines: [...new Set(input.disciplines)],
        message: input.message.trim(),
        status: "pending" as const,
        createdAt: ctx.now(),
      };
      ctx.state.orgRequests.push(request);
      ctx.commit();
      return orgRequestView(request);
    },

    async requestProjectAccess(projectId, message) {
      await ctx.wait();
      const { user } = requireSession(ctx);
      const project = ctx.options.projects.find(projectId);
      if (project === undefined) throw new NotFoundError("This project");
      const text = message.trim();
      if (text.length > MESSAGE_MAX)
        throw new ValidationError("message", `Use ${String(MESSAGE_MAX)} characters or fewer.`);
      const now = ctx.now();
      const open = ctx.state.projectRequests.find(
        (r) =>
          r.projectId === projectId && r.userId === user.id && requestStatus(r, now) === "pending",
      );
      if (open !== undefined) return projectRequestView(ctx, open);
      const request = {
        id: `preq-${ctx.token().slice(0, 12)}`,
        organisationId: project.organisationId,
        projectId,
        projectName: project.name,
        userId: user.id,
        message: text,
        status: "pending" as const,
        createdAt: now,
        expiresAt: now + IDENTITY_POLICY.projectRequestMs,
      };
      ctx.state.projectRequests.push(request);
      ctx.commit();
      return projectRequestView(ctx, request);
    },

    async myProjectAccessRequest(projectId) {
      await ctx.wait();
      const { user } = requireSession(ctx);
      const latest = ctx.state.projectRequests
        .filter((r) => r.projectId === projectId && r.userId === user.id)
        .sort((a, b) => b.createdAt - a.createdAt)[0];
      return latest === undefined ? null : projectRequestView(ctx, latest);
    },

    async listProjectAccessRequests() {
      await ctx.wait();
      const { org } = requirePermission(ctx, "project.manage_access");
      const now = ctx.now();
      return ctx.state.projectRequests
        .filter((r) => r.organisationId === org.id && requestStatus(r, now) === "pending")
        .sort((a, b) => b.createdAt - a.createdAt)
        .map((r) => projectRequestView(ctx, r));
    },

    async decideProjectAccessRequest(requestId, decision) {
      await ctx.wait();
      const { org } = requirePermission(ctx, "project.manage_access");
      const request = ctx.state.projectRequests.find(
        (r) => r.id === requestId && r.organisationId === org.id,
      );
      if (request === undefined) throw new NotFoundError("Request");
      if (requestStatus(request, ctx.now()) !== "pending")
        throw new ValidationError("request", "This request was already answered or has expired.");
      if ("decline" in decision) {
        request.status = "declined";
      } else {
        request.status = "approved";
        request.role = decision.approve;
        ctx.state.projectGrants = ctx.state.projectGrants.filter(
          (g) => !(g.projectId === request.projectId && g.userId === request.userId),
        );
        ctx.state.projectGrants.push({
          organisationId: org.id,
          projectId: request.projectId,
          userId: request.userId,
          role: decision.approve,
        });
        // Someone from outside the organisation joins as a guest who sees only granted projects (FR-ADMIN-024).
        if (roleIn(ctx, request.userId, org.id) === undefined) {
          ctx.state.memberships.push({
            userId: request.userId,
            organisationId: org.id,
            role: "guest",
            joinedAt: ctx.now(),
          });
        }
      }
      ctx.commit();
    },
  };
}
